<script setup lang="ts">
import Logo from "../../assets/logo.svg";
import { getRouteXDashboard } from "../../views/Auth/Dashboard.vue";
import HeaderMenu from "../../widgets/HeaderMenuWidget.vue";
import AccountBlockProfileWidget from "../../widgets/AccountBlockProfileWidget.vue";
import DarkModeWidget from "../../widgets/DarkModeWidget.vue";
import {usePersonStore} from "@/store";
import {storeToRefs} from "pinia";
import {computed, ref} from "vue";
import {RouteNames} from "@/utils/constants.ts";
import {useRoute} from "vue-router";

const route = useRoute();
const personStore = usePersonStore();
const { person } = storeToRefs(personStore);

// Show menu only on pages with isXLayout meta (Dashboard and other authenticated pages)
const showMenu = computed(() => person.value !== null && route.meta.isXLayout === true);

// Check if user is authenticated
const isAuthenticated = computed(() => person.value !== null);

// Mobile menu state
const isMobileMenuOpen = ref(false);

const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value;
};

const closeMobileMenu = () => {
  isMobileMenuOpen.value = false;
};
</script>

<template>
  <header class="w-screen">
    <nav class="mx-auto h-20 mb-6 p-2 sm:p-4 " :class="{'shadow': true}">
      <div class="mx-auto gap-4 h-full items-center flex space-between buixl:w-[1200px]">
        <div class="flex-shrink flex items-center justify-start">
          <RouterLink :to="getRouteXDashboard()">
            <img
              :src="Logo"
              alt="Moneterum"
              width="auto"
              height="100%"
            />
          </RouterLink>
        </div>
        <div
          class="hidden lg:flex flex-1 items-center justify-center gap-6 text-center uppercase text-sm/[21px] font-semibold font-['Poppins'] text-nowrap"
        >
          <HeaderMenu v-if="showMenu" />
        </div>

        <div class="flex-1 lg:hidden"></div>

        <!-- Dark mode toggle: hidden on mobile when burger menu is available -->
        <DarkModeWidget v-if="!showMenu" />
        <!-- Show on desktop only when menu is available -->
        <div v-else class="hidden lg:block">
          <DarkModeWidget />
        </div>

        <!-- For non-authenticated users: Show only Login button -->
        <div v-if="!isAuthenticated" class="flex space-x-2 md:space-x-3 justify-center items-center pl-2">
          <RouterLink
            :to="{ name: RouteNames.AUTH_LOGIN }"
            class="px-3 py-2 md:px-4 md:py-2 text-sm md:text-base font-semibold bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Login
          </RouterLink>
        </div>

        <!-- For authenticated users: Show account widget and burger menu -->
        <div v-else class="flex space-x-3 md:space-x-5 justify-center items-center pl-2">
          <AccountBlockProfileWidget />

          <!-- Burger menu button (only on mobile and when menu should be shown) -->
          <button
            v-if="showMenu"
            @click="toggleMobileMenu"
            class="lg:hidden p-2 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            aria-label="Toggle menu"
          >
            <svg
              class="h-6 w-6"
              :class="{ 'hidden': isMobileMenuOpen, 'block': !isMobileMenuOpen }"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            <svg
              class="h-6 w-6"
              :class="{ 'block': isMobileMenuOpen, 'hidden': !isMobileMenuOpen }"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </nav>

    <!-- Mobile menu overlay -->
    <Transition name="fade">
      <div
        v-if="showMenu && isMobileMenuOpen"
        class="fixed inset-0 z-50 lg:hidden bg-white dark:bg-gray-900"
      >
        <!-- Close button -->
        <button
          @click="closeMobileMenu"
          class="absolute top-6 right-6 p-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors"
          aria-label="Close menu"
        >
          <svg
            class="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <!-- Menu content -->
        <div class="flex flex-col items-center justify-center h-full px-8">
          <nav class="flex flex-col items-center space-y-8 mb-12">
            <RouterLink
              :to="{ name: RouteNames.X_DASHBOARD }"
              @click="closeMobileMenu"
              class="text-xl font-semibold uppercase tracking-wide transition-colors"
              :class="route.name === RouteNames.X_DASHBOARD ? 'text-blue-600 dark:text-blue-400' : 'text-gray-900 dark:text-gray-100'"
            >
              Dashboard
            </RouterLink>
            <RouterLink
              :to="{ name: RouteNames.X_ACCOUNTS_FIAT }"
              @click="closeMobileMenu"
              class="text-xl font-semibold uppercase tracking-wide transition-colors"
              :class="route.name === RouteNames.X_ACCOUNTS_FIAT ? 'text-blue-600 dark:text-blue-400' : 'text-gray-900 dark:text-gray-100'"
            >
              Cash Accounts
            </RouterLink>
            <RouterLink
              :to="{ name: RouteNames.X_ACCOUNTS_CRYPTO }"
              @click="closeMobileMenu"
              class="text-xl font-semibold uppercase tracking-wide transition-colors"
              :class="route.name === RouteNames.X_ACCOUNTS_CRYPTO ? 'text-blue-600 dark:text-blue-400' : 'text-gray-900 dark:text-gray-100'"
            >
              Crypto Wallets
            </RouterLink>
            <RouterLink
              :to="{ name: RouteNames.X_ACCOUNTS_CARD }"
              @click="closeMobileMenu"
              class="text-xl font-semibold uppercase tracking-wide transition-colors"
              :class="route.name === RouteNames.X_ACCOUNTS_CARD ? 'text-blue-600 dark:text-blue-400' : 'text-gray-900 dark:text-gray-100'"
            >
              Cards
            </RouterLink>
          </nav>

          <!-- Dark mode toggle at bottom -->
          <div class="absolute bottom-12">
            <DarkModeWidget />
          </div>
        </div>
      </div>
    </Transition>
  </header>
</template>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
